import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { agrogestao } from '@/api/agrogestaoClient';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";

export function LicencasAdmin({ selectedPropId, refresh }) {
    const [busca, setBusca] = useState('');
    const [plano, setPlano] = useState('todos');
    const [status, setStatus] = useState('todos');
    const [periodoTipo, setPeriodoTipo] = useState('vencimento');
    const [inicio, setInicio] = useState('');
    const [fim, setFim] = useState('');
    const [page, setPage] = useState(1);
    const [novoPlano, setNovoPlano] = useState('mensal');
    const [autoRenova, setAutoRenova] = useState(false);
    const [limiteProps, setLimiteProps] = useState('');
    const [limiteUsers, setLimiteUsers] = useState('');
    const [modulosTxt, setModulosTxt] = useState('');
    const [pixDataSel, setPixDataSel] = useState(null);
    const [pixLoading, setPixLoading] = useState(false);
    const [pixError, setPixError] = useState('');
    const [vencimentoManual, setVencimentoManual] = useState('');
    const pageSize = 20;

    const { data: licencas = [], isLoading } = useQuery({
        queryKey: ['licencas_admin', refresh],
        queryFn: async () => {
            const { data, error } = await agrogestao.client
                .from('licencas')
                .select('*')
                .order('valid_until', { ascending: false })
                .limit(5000);
            if (error) return [];
            return data || [];
        }
    });

    const { data: propriedades = [] } = useQuery({
        queryKey: ['propriedades_admin'],
        queryFn: async () => {
            const { data, error } = await agrogestao.client.from('propriedades').select('id, nome, created_by').order('nome', { ascending: true }).limit(1000);
            if (error) return [];
            return data || [];
        }
    });

    const { data: produtores = [] } = useQuery({
        queryKey: ['users_admin'],
        queryFn: async () => {
            const { data, error } = await agrogestao.client.from('users').select('email, full_name').limit(1000);
            if (error) return [];
            return data || [];
        }
    });

    const propsById = useMemo(() => {
        const map = {};
        propriedades.forEach(p => { map[String(p.id)] = p; });
        return map;
    }, [propriedades]);

    const usersByEmail = useMemo(() => {
        const map = {};
        produtores.forEach(u => { map[String(u.email)] = u; });
        return map;
    }, [produtores]);

    const licenseIds = useMemo(() => licencas.map(l => l.id).filter(Boolean), [licencas]);

    const { data: faturas = [] } = useQuery({
        queryKey: ['faturas_by_licenca', licenseIds.join(',')],
        queryFn: async () => {
            if (!licenseIds || licenseIds.length === 0) return [];
            const { data, error } = await agrogestao.client
                .from('faturas')
                .select('id, licenca_id, status, valor_centavos, created_date, paga_em, vencimento, descricao')
                .in('licenca_id', licenseIds)
                .order('created_date', { ascending: false })
                .limit(1000);
            if (error) return [];
            return data || [];
        },
        enabled: licenseIds.length > 0
    });

    const faturasByLicenca = useMemo(() => {
        const map = {};
        faturas.forEach(f => {
            const key = String(f.licenca_id || '');
            if (!map[key]) map[key] = [];
            map[key].push(f);
        });
        return map;
    }, [faturas]);

    const hoje = new Date();

    const filtered = useMemo(() => {
        const arr = (licencas || []).filter(l => {
            if (plano !== 'todos' && l.plano !== plano) return false;
            if (status !== 'todos') {
                if (status === 'ativo') {
                    const exp = l.valid_until ? new Date(l.valid_until) : null;
                    const ativa = l.status === 'ativo' && (exp ? exp >= hoje : true);
                    if (!ativa) return false;
                } else if (status === 'bloqueado') {
                    if (l.status !== 'bloqueado') return false;
                } else if (status === 'vencido') {
                    const exp = l.valid_until ? new Date(l.valid_until) : null;
                    const vencido = l.status === 'ativo' && exp && exp < hoje;
                    if (!vencido) return false;
                } else if (status === 'trial') {
                    if (l.plano !== 'trial') return false;
                }
            }
            if (busca) {
                const b = busca.toLowerCase();
                const prop = propsById[String(l.propriedade_id || '')];
                const produtor = prop ? usersByEmail[String(prop.created_by || '')] : null;
                const campos = [String(l.id || ''), String(l.plano || ''), String(l.status || ''), String(prop?.nome || ''), String(prop?.id || ''), String(produtor?.full_name || ''), String(produtor?.email || '')].map(x => x.toLowerCase());
                if (!campos.some(c => c.includes(b))) return false;
            }
            if (inicio || fim) {
                let d = null;
                if (periodoTipo === 'vencimento') d = l.valid_until ? new Date(l.valid_until) : null;
                else d = l.paid_at ? new Date(l.paid_at) : (l.valid_until ? new Date(l.valid_until) : (l.created_at ? new Date(l.created_at) : null));
                if (inicio) {
                    const di = new Date(inicio);
                    if (d && d < di) return false;
                }
                if (fim) {
                    const df = new Date(fim);
                    if (d && d > df) return false;
                }
            }
            return true;
        });
        arr.sort((a, b) => {
            const av = a.valid_until ? new Date(a.valid_until).getTime() : 0;
            const bv = b.valid_until ? new Date(b.valid_until).getTime() : 0;
            return bv - av;
        });
        return arr;
    }, [licencas, selectedPropId, plano, status, busca, inicio, fim, periodoTipo, propsById, usersByEmail]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const pageData = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page]);

    const [detalheOpen, setDetalheOpen] = useState(false);
    const [licSel, setLicSel] = useState(null);

    React.useEffect(() => {
        if (licSel) {
            setNovoPlano(licSel.plano || 'mensal');
            setAutoRenova(!!licSel.auto_renova);
            setLimiteProps(String(licSel.limite_propriedades ?? ''));
            setLimiteUsers(String(licSel.limite_usuarios ?? ''));
            setModulosTxt(String(licSel.modulos ?? ''));
            setPixDataSel(null);
            setPixError('');
            
            if (licSel.status === 'bloqueado' || (licSel.valid_until && new Date(licSel.valid_until) < new Date())) {
                const defaultUntil = new Date();
                defaultUntil.setDate(defaultUntil.getDate() + (licSel.plano === 'anual' ? 365 : licSel.plano === 'trial' ? 15 : 30));
                setVencimentoManual(defaultUntil.toISOString().split('T')[0]);
            } else {
                setVencimentoManual(licSel.valid_until || '');
            }
        }
    }, [licSel]);

    const abrirDetalhe = (l) => { setLicSel(l); setDetalheOpen(true); };

    const exportCsv = async () => {
        const header = ['Licenca', 'Plano', 'Status', 'Vencimento', 'UltimaRenovacao', 'ProdutorNome', 'ProdutorEmail', 'Propriedade'];
        const rows = filtered.map(l => {
            const prop = propsById[String(l.propriedade_id || '')];
            const produtor = prop ? usersByEmail[String(prop.created_by || '')] : null;
            const fats = faturasByLicenca[String(l.id || '')] || [];
            const pago = fats.find(f => f.status === 'pago');
            const ult = pago?.paga_em ? format(new Date(pago.paga_em), 'dd/MM/yyyy') : '-';
            return [String(l.id || ''), l.plano || '', l.status || '', l.valid_until ? format(new Date(l.valid_until), 'dd/MM/yyyy') : '', ult, produtor?.full_name || '', produtor?.email || '', prop?.nome || String(l.propriedade_id || '')];
        });
        const csv = [header.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '"')}"`).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'licencas.csv';
        a.click();
        URL.revokeObjectURL(url);
        try { await agrogestao.access.logAudit({ acao: 'exportar_csv', table: 'licencas', status: 'success' }); } catch { }
    };

    const exportPdf = async () => {
        const w = window.open('', '_blank');
        if (!w) return;
        const rows = filtered.map(l => {
            const prop = propsById[String(l.propriedade_id || '')];
            const produtor = prop ? usersByEmail[String(prop.created_by || '')] : null;
            const fats = faturasByLicenca[String(l.id || '')] || [];
            const pago = fats.find(f => f.status === 'pago');
            const ult = pago?.paga_em ? format(new Date(pago.paga_em), 'dd/MM/yyyy') : '-';
            return `<tr><td>${String(l.id || '')}</td><td>${l.plano || ''}</td><td>${l.status || ''}</td><td>${l.valid_until ? format(new Date(l.valid_until), 'dd/MM/yyyy') : ''}</td><td>${ult}</td><td>${produtor?.full_name || ''}</td><td>${produtor?.email || ''}</td><td>${prop?.nome || String(l.propriedade_id || '')}</td></tr>`;
        }).join('');
        w.document.write(`<html><head><title>Licencas</title></head><body><h2>Licenças</h2><table border="1" cellspacing="0" cellpadding="4"><thead><tr><th>Licença</th><th>Plano</th><th>Status</th><th>Vencimento</th><th>Última Renovação</th><th>Produtor</th><th>Email</th><th>Propriedade</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
        w.document.close();
        w.focus();
        w.print();
        try { await agrogestao.access.logAudit({ acao: 'exportar_pdf', table: 'licencas', status: 'success' }); } catch { }
    };

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                <Input placeholder="Buscar" value={busca} onChange={(e) => setBusca(e.target.value)} />
                <Select value={plano} onValueChange={setPlano}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="todos">Todos os planos</SelectItem>
                        <SelectItem value="trial">Trial</SelectItem>
                        <SelectItem value="mensal">Mensal</SelectItem>
                        <SelectItem value="anual">Anual</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="todos">Todos os status</SelectItem>
                        <SelectItem value="ativo">Ativos</SelectItem>
                        <SelectItem value="vencido">Vencidos</SelectItem>
                        <SelectItem value="bloqueado">Bloqueados</SelectItem>
                        <SelectItem value="trial">Trial</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={periodoTipo} onValueChange={setPeriodoTipo}>
                    <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="vencimento">Período: Vencimento</SelectItem>
                        <SelectItem value="cadastro">Período: Cadastro</SelectItem>
                    </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                    <Label>Início</Label>
                    <Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
                </div>
                <div className="flex items-center gap-2">
                    <Label>Fim</Label>
                    <Input type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
                </div>
                <Button onClick={exportCsv} className="bg-indigo-600 hover:bg-indigo-700">Exportar CSV</Button>
                <Button onClick={exportPdf} variant="outline">Exportar PDF</Button>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b">
                            <th className="text-left py-3 px-4">Licença</th>
                            <th className="text-left py-3 px-4">Plano</th>
                            <th className="text-left py-3 px-4">Status</th>
                            <th className="text-left py-3 px-4">Vencimento</th>
                            <th className="text-left py-3 px-4">Última Renovação</th>
                            <th className="text-left py-3 px-4">Produtor</th>
                            <th className="text-left py-3 px-4">Propriedade</th>
                            <th className="text-left py-3 px-4">Pagamentos</th>
                            <th className="text-left py-3 px-4">Renovações</th>
                            <th className="text-left py-3 px-4">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={10} className="py-6 text-center">Carregando...</td></tr>
                        ) : pageData.length === 0 ? (
                            <tr><td colSpan={10} className="py-6 text-center text-stone-500">Nenhuma licença encontrada</td></tr>
                        ) : pageData.map((l) => {
                            const prop = propsById[String(l.propriedade_id || '')];
                            const produtor = prop ? usersByEmail[String(prop.created_by || '')] : null;
                            const fats = faturasByLicenca[String(l.id || '')] || [];
                            const pago = fats.find(f => f.status === 'pago');
                            const ult = pago?.paga_em ? format(new Date(pago.paga_em), 'dd/MM/yyyy') : '-';
                            const ren = licencas.filter(x => String(x.propriedade_id || '') === String(l.propriedade_id || '')).length;
                            return (
                                <tr key={l.id} className="border-b">
                                    <td className="py-3 px-4 font-mono text-xs">{String(l.id || '')}</td>
                                    <td className="py-3 px-4">{l.plano || '-'}</td>
                                    <td className="py-3 px-4">
                                        <Badge className={l.status === 'ativo' ? 'bg-green-100 text-green-700' : l.status === 'bloqueado' ? 'bg-red-100 text-red-700' : 'bg-stone-100 text-stone-700'}>{l.status || '-'}</Badge>
                                    </td>
                                    <td className="py-3 px-4">{l.valid_until ? format(new Date(l.valid_until), 'dd/MM/yyyy') : '-'}</td>
                                    <td className="py-3 px-4">{ult}</td>
                                    <td className="py-3 px-4">{produtor?.full_name || produtor?.email || '-'}</td>
                                    <td className="py-3 px-4">{prop?.nome || String(l.propriedade_id || '')}</td>
                                    <td className="py-3 px-4 text-xs">{fats.length} registro(s)</td>
                                    <td className="py-3 px-4 text-xs">{ren}</td>
                                    <td className="py-3 px-4">
                                        <Button onClick={() => abrirDetalhe(l)} variant="outline">Ver detalhes</Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="flex items-center gap-2 justify-end">
                <Button onClick={() => setPage(p => Math.max(1, p - 1))} variant="outline">Anterior</Button>
                <div className="text-sm">Página {page} de {totalPages}</div>
                <Button onClick={() => setPage(p => Math.min(totalPages, p + 1))} variant="outline">Próxima</Button>
            </div>
            <Dialog open={detalheOpen} onOpenChange={setDetalheOpen}>
                <DialogContent className="max-w-5xl w-[95vw] sm:w-auto max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Detalhes da Licença</DialogTitle>
                        <DialogDescription></DialogDescription>
                    </DialogHeader>
                    {licSel && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                                <Card className="bg-emerald-50 border-emerald-200">
                                    <CardHeader><CardTitle className="text-sm">Licença</CardTitle></CardHeader>
                                    <CardContent>
                                        <div className="text-sm">ID: <span className="font-mono">{String(licSel.id || '')}</span></div>
                                        <div className="text-sm">Plano: {licSel.plano || '-'}</div>
                                        <div className="text-sm">Status: {licSel.status || '-'}</div>
                                        <div className="text-sm">Vencimento: {licSel.valid_until ? format(new Date(licSel.valid_until), 'dd/MM/yyyy') : '-'}</div>
                                    </CardContent>
                                </Card>
                                <Card className="bg-teal-50 border-teal-200">
                                    <CardHeader><CardTitle className="text-sm">Propriedade</CardTitle></CardHeader>
                                    <CardContent>
                                        <div className="text-sm">Nome: {propsById[String(licSel.propriedade_id || '')]?.nome || '-'}</div>
                                    </CardContent>
                                </Card>
                                <Card className="bg-sky-50 border-sky-200">
                                    <CardHeader><CardTitle className="text-sm">Produtor</CardTitle></CardHeader>
                                    <CardContent>
                                        <div className="text-sm">Nome: {(() => { const prop = propsById[String(licSel.propriedade_id || '')]; const u = prop ? usersByEmail[String(prop.created_by || '')] : null; return u?.full_name || '-'; })()}</div>
                                        <div className="text-sm">Email: {(() => { const prop = propsById[String(licSel.propriedade_id || '')]; const u = prop ? usersByEmail[String(prop.created_by || '')] : null; return u?.email || '-'; })()}</div>
                                    </CardContent>
                                </Card>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Card className="bg-emerald-50 text-stone-900 border-emerald-200">
                                    <CardHeader><CardTitle className="text-sm">Ações</CardTitle></CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="flex flex-col gap-2 p-3 bg-white/50 rounded-lg border border-stone-100">
                                            <Label className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Vencimento para reativação</Label>
                                            <div className="flex gap-2 items-center">
                                                <Input 
                                                    type="date" 
                                                    value={vencimentoManual} 
                                                    onChange={(e) => setVencimentoManual(e.target.value)} 
                                                    className="w-full sm:w-44 bg-white" 
                                                />
                                                <span className="text-xs text-stone-400 italic">Padrão: +{licSel.plano === 'anual' ? '1 ano' : licSel.plano === 'trial' ? '15 dias' : '30 dias'}</span>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <Button className="bg-amber-600 hover:bg-amber-700" onClick={async () => { 
                                                const now = new Date();
                                                const today = now.toISOString().split('T')[0];
                                                await agrogestao.client.from('licencas').update({ 
                                                    status: 'bloqueado', 
                                                    valid_until: today,
                                                    updated_at: now.toISOString()
                                                }).eq('id', licSel.id); 
                                                try { await agrogestao.access.logAudit({ table: 'licencas', acao: 'bloquear_licenca', details: { licenca_id: licSel.id, propriedade_id: licSel.propriedade_id }, status: 'success' }); } catch { }; setDetalheOpen(false); 
                                            }}>Suspender</Button>
                                            <Button className="bg-green-600 hover:bg-green-700" onClick={async () => { 
                                                if (!vencimentoManual) { alert('Informe a data de vencimento'); return; }
                                                const now = new Date();
                                                await agrogestao.client.from('licencas').update({ 
                                                    status: 'ativo', 
                                                    valid_until: vencimentoManual,
                                                    updated_at: now.toISOString()
                                                }).eq('id', licSel.id); 
                                                try { await agrogestao.access.logAudit({ table: 'licencas', acao: 'reativar_licenca', details: { licenca_id: licSel.id, propriedade_id: licSel.propriedade_id, valid_until: vencimentoManual }, status: 'success' }); } catch { }; 
                                                setDetalheOpen(false); 
                                            }}>Reativar (manual)</Button>
                                            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={async () => { setPixLoading(true); setPixError(''); setPixDataSel(null); try { const res = await agrogestao.functions.invoke('criarQRCodePixLicenca', { body: { propriedadeId: licSel.propriedade_id, plano: licSel.plano } }); const json = res?.data || res; if (json?.success) { setPixDataSel({ brCodeBase64: json?.brCodeBase64 || json?.qrcode_base64, brCode: json?.brCode || json?.pix_brcode }); } else { setPixError(String(json?.error || 'falha_pix')); } } catch (err) { setPixError(String((err?.message) || err)); } finally { setPixLoading(false); } }}> {pixLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Renovar via PIX'} </Button>
                                            <Button variant="destructive" className="bg-red-600 hover:bg-red-700" onClick={async () => {
                                                if (!window.confirm("AVISO CRÍTICO: Esta ação excluirá permanentemente esta licença e TODAS as faturas vinculadas a ela. Esta ação não pode ser desfeita. Deseja continuar?")) return;
                                                
                                                try {
                                                    // 1. Remover faturas associadas
                                                    const { error: errFaturas } = await agrogestao.client
                                                        .from('faturas')
                                                        .delete()
                                                        .eq('licenca_id', licSel.id);
                                                    
                                                    if (errFaturas) throw errFaturas;

                                                    // 2. Remover a licença
                                                    const { error: errLic } = await agrogestao.client
                                                        .from('licencas')
                                                        .delete()
                                                        .eq('id', licSel.id);
                                                    
                                                    if (errLic) throw errLic;

                                                    // 3. Auditoria
                                                    const { data: { user } } = await agrogestao.auth.getUser();
                                                    try { 
                                                        await agrogestao.access.logAudit({ 
                                                            table: 'licencas', 
                                                            acao: 'excluir_licenca', 
                                                            details: { 
                                                                licenca_id: licSel.id, 
                                                                propriedade_id: licSel.propriedade_id,
                                                                excluido_por: user?.email || 'unknown'
                                                            }, 
                                                            status: 'success' 
                                                        }); 
                                                    } catch { };

                                                    // 4. Cleanup UI
                                                    setDetalheOpen(false);
                                                    if (typeof refresh === 'function') refresh();
                                                } catch (err) {
                                                    alert('Erro ao excluir licença: ' + (err.message || err));
                                                }
                                            }}>Excluir Definitivamente</Button>
                                        </div>
                                        {pixError && <div className="text-red-600">{pixError}</div>}
                                        {pixDataSel && (
                                            <div className="space-y-2">
                                                {pixDataSel.brCodeBase64 && <img src={pixDataSel.brCodeBase64} alt="QR PIX" className="w-40 h-40" />}
                                                {pixDataSel.brCode && <div className="bg-stone-100 p-2 rounded break-all">{pixDataSel.brCode}</div>}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                                <Card className="bg-sky-50 text-stone-900 border-sky-200">
                                    <CardHeader><CardTitle className="text-sm">Upgrade/Downgrade</CardTitle></CardHeader>
                                    <CardContent className="space-y-2">
                                        <div className="flex gap-2 items-center">
                                            <Label>Plano</Label>
                                            <Select value={novoPlano} onValueChange={setNovoPlano}>
                                                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="trial">Trial</SelectItem>
                                                    <SelectItem value="mensal">Mensal</SelectItem>
                                                    <SelectItem value="anual">Anual</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <Button className="bg-stone-700 hover:bg-stone-800" onClick={async () => { const until = new Date(); until.setDate(until.getDate() + (novoPlano === 'anual' ? 365 : novoPlano === 'trial' ? 15 : 30)); await agrogestao.client.from('licencas').insert({ propriedade_id: licSel.propriedade_id, plano: novoPlano, status: 'ativo', valid_until: until.toISOString().split('T')[0], source: 'manual_admin' }); try { await agrogestao.access.logAudit({ table: 'licencas', acao: 'alterar_plano', details: { licenca_id: licSel.id, propriedade_id: licSel.propriedade_id, novo_plano: novoPlano }, status: 'success' }); } catch { }; setDetalheOpen(false); }}>Aplicar</Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Card className="bg-lime-50 text-stone-900 border-lime-200">
                                    <CardHeader><CardTitle className="text-sm">Configurações de limites</CardTitle></CardHeader>
                                    <CardContent className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Label>Renovação automática</Label>
                                            <input type="checkbox" checked={autoRenova} onChange={(e) => setAutoRenova(e.target.checked)} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <Label>Limite de propriedades</Label>
                                                <Input placeholder="ex: 3" value={limiteProps} onChange={(e) => setLimiteProps(e.target.value)} />
                                            </div>
                                            <div>
                                                <Label>Limite de usuários</Label>
                                                <Input placeholder="ex: 10" value={limiteUsers} onChange={(e) => setLimiteUsers(e.target.value)} />
                                            </div>
                                        </div>
                                        <div>
                                            <Label>Módulos habilitados</Label>
                                            <Input placeholder="ex: financeiro, suporte" value={modulosTxt} onChange={(e) => setModulosTxt(e.target.value)} />
                                        </div>
                                        <Button className="bg-green-600 hover:bg-green-700" onClick={async () => { await agrogestao.client.from('licencas').update({ auto_renova: !!autoRenova, limite_propriedades: limiteProps ? Number(limiteProps) : null, limite_usuarios: limiteUsers ? Number(limiteUsers) : null, modulos: modulosTxt || null }).eq('id', licSel.id); try { await agrogestao.access.logAudit({ table: 'licencas', acao: 'atualizar_limites', details: { licenca_id: licSel.id, propriedade_id: licSel.propriedade_id }, status: 'success' }); } catch { }; setDetalheOpen(false); }}>Salvar</Button>
                                    </CardContent>
                                </Card>
                                <Card className="bg-rose-50 text-stone-900 border-rose-200">
                                    <CardHeader><CardTitle className="text-sm">Histórico</CardTitle></CardHeader>
                                    <CardContent>
                                        <div className="text-stone-600">Últimas movimentações da licença</div>
                                        <div className="text-stone-500 text-xs">Alterações são registradas em auditoria</div>
                                    </CardContent>
                                </Card>
                            </div>
                            <Card className="bg-amber-50 text-stone-900 border-amber-200">
                                <CardHeader><CardTitle className="text-sm">Pagamentos</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b">
                                                    <th className="text-left py-2 px-2">Criada</th>
                                                    <th className="text-left py-2 px-2">Vencimento</th>
                                                    <th className="text-left py-2 px-2">Descrição</th>
                                                    <th className="text-right py-2 px-2">Valor</th>
                                                    <th className="text-left py-2 px-2">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const list = faturasByLicenca[String(licSel.id || '')] || [];
                                                    if (list.length === 0) return (
                                                        <tr><td colSpan={5} className="py-4 text-center text-stone-500">Nenhum pagamento encontrado</td></tr>
                                                    );
                                                    return list.map(f => (
                                                        <tr key={f.id} className="border-b">
                                                            <td className="py-2 px-2">{f.created_date ? format(new Date(f.created_date), 'dd/MM/yyyy HH:mm') : '-'}</td>
                                                            <td className="py-2 px-2">{f.vencimento ? format(new Date(f.vencimento), 'dd/MM/yyyy') : '-'}</td>
                                                            <td className="py-2 px-2">{f.descricao || '-'}</td>
                                                            <td className="py-2 px-2 text-right">{(Number(f.valor_centavos || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                                            <td className="py-2 px-2"><Badge className={f.status === 'pago' ? 'bg-emerald-100 text-emerald-700' : f.status === 'pendente' ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-700'}>{f.status}</Badge></td>
                                                        </tr>
                                                    ));
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="bg-violet-50 text-stone-900 border-violet-200">
                                <CardHeader><CardTitle className="text-sm">Renovações</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b">
                                                    <th className="text-left py-2 px-2">Licença</th>
                                                    <th className="text-left py-2 px-2">Plano</th>
                                                    <th className="text-left py-2 px-2">Status</th>
                                                    <th className="text-left py-2 px-2">Vencimento</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const list = (licencas || []).filter(x => String(x.propriedade_id || '') === String(licSel.propriedade_id || '')).sort((a, b) => {
                                                        const av = a.valid_until ? new Date(a.valid_until).getTime() : 0;
                                                        const bv = b.valid_until ? new Date(b.valid_until).getTime() : 0;
                                                        return bv - av;
                                                    });
                                                    if (list.length === 0) return (
                                                        <tr><td colSpan={4} className="py-4 text-center text-stone-500">Nenhuma renovação registrada</td></tr>
                                                    );
                                                    return list.map(r => (
                                                        <tr key={r.id} className="border-b">
                                                            <td className="py-2 px-2 font-mono text-xs">{String(r.id || '')}</td>
                                                            <td className="py-2 px-2">{r.plano || '-'}</td>
                                                            <td className="py-2 px-2">{r.status || '-'}</td>
                                                            <td className="py-2 px-2">{r.valid_until ? format(new Date(r.valid_until), 'dd/MM/yyyy') : '-'}</td>
                                                        </tr>
                                                    ));
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
